import { useRef, useState, useEffect, useCallback } from "react";
import { IoMdCloseCircle } from "react-icons/io";
import { FiRefreshCcw } from "react-icons/fi";
import placeHolderImage from "../../Assets/Images/dummyImage.webp";
import Timer from "./Timer";
import { subscribeToTracks } from "../../utils/avatarHelper";

function AvatarVideo({
  avatar,
  videoLayerRef,
  isVideoLoading,
  avatarReady,
  heygenError,
  consultationId,
  isCompleted,
  setShowCloseConfirmModal,
  restartLiveAvatarSession,
  isSmallScreen,
  setIsSmallScreen,
  headers,
  processPayment,
  hasVideo,
  setHasVideo
}) {
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePointerDown = (e) => {
    if (!isSmallScreen || !dragRef.current) return;
    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
    dragRef.current.style.transition = "none";
  };

  const handlePointerMove = useCallback((e) => {
    if (!isDragging.current || !dragRef.current) return;
    if (e.cancelable) e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const x = clientX - dragOffset.current.x;
    const y = clientY - dragOffset.current.y;

    const maxX = window.innerWidth - dragRef.current.offsetWidth;
    const maxY = window.innerHeight - dragRef.current.offsetHeight;

    const clampedX = Math.max(0, Math.min(x, maxX));
    const clampedY = Math.max(0, Math.min(y, maxY));

    dragRef.current.style.left = `${clampedX}px`;
    dragRef.current.style.top = `${clampedY}px`;
    dragRef.current.style.right = "auto";
    dragRef.current.style.bottom = "auto";
  }, []);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
    if (dragRef.current) dragRef.current.style.transition = "all 0.3s ease";
  }, []);

  useEffect(() => {
    if (!isSmallScreen) return;

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
    };
  }, [isSmallScreen, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    if (!avatar) { return }

    const room = avatar?.room;
    if (!room) return;

    return subscribeToTracks(room, {
      onVideoTrack: (track) => {
        const videoLayer = videoLayerRef.current;
        if (videoLayer) {
          while (videoLayer.firstChild) { videoLayer.removeChild(videoLayer.firstChild) }
          const element = track.attach();
          element.style.width = "100%";
          element.style.height = "100%";
          element.style.objectFit = isSmallScreen ? "contain" : "cover";
          videoLayer.appendChild(element);
          setHasVideo(true);
        }
      },
      onTrackUnsubscribed: (track) => {
        if (track.kind === "video") {
          track.detach().forEach((el) => el.remove());
          setHasVideo(false);
        }
      }
    });
  }, [avatar?.room]);


  // useEffect(() => {
  //   if (!avatar) return;
  //   const handleStop = async () => { await restartLiveAvatarSession() };
  //   avatar.on("session.stopped", handleStop);
  //   return () => { avatar.off("session.stopped", handleStop) };
  // }, [avatar, restartLiveAvatarSession]);
  // useEffect(() => {
  //   if (!avatar?._sessionInfo?.max_session_duration) return;
  //   const duration = avatar._sessionInfo.max_session_duration;
  //   const timer = setTimeout(() => { restartLiveAvatarSession(); }, (duration - 5) * 1000);
  //   return () => clearTimeout(timer);
  // }, [avatar, restartLiveAvatarSession]);

  const isActiveConsultation = consultationId && !isCompleted;
  const isDisconnected = avatar?._state === "DISCONNECTED";
  const hasConnectionIssue = !avatar || isDisconnected || !hasVideo;
  const showMobileControls = isSmallScreen && isActiveConsultation;
  const showDesktopControls = !isSmallScreen && isActiveConsultation;
  const canClose = isActiveConsultation;
  const canReconnect = hasConnectionIssue && isActiveConsultation;

  return (
    <div className="consult_ai_doc">
      <div ref={dragRef}
        style={isSmallScreen ?
          {
            position: "fixed",
            zIndex: 9999,
            width: "331px",
            height: "186px",
            right: "20px",
            top: "80px",
            borderRadius: "10px",
            overflow: "hidden",
            cursor: isDragging.current ? "grabbing" : "grab"
          }
          :
          { position: "relative", zIndex: 100 }
        }
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        {showMobileControls && (
          <>
            {canClose && <button className="mobile_btn close_btn" onClick={(e) => { e.stopPropagation(); setShowCloseConfirmModal(true); }}>
              <IoMdCloseCircle size={28} />
            </button>}
            {canReconnect && (
              <button
                className="mobile_btn refresh_btn"
                onClick={(e) => { e.stopPropagation(); restartLiveAvatarSession(); }}
                disabled={isVideoLoading}
              >
                <FiRefreshCcw size={26} className={isVideoLoading ? "spin" : ""} />
              </button>
            )}
          </>
        )}

        <div
          id="avatar-container"
          style={{
            width: "100%",
            height: isSmallScreen ? "186px" : "85vh",
            position: "relative",
            background: 'silver',
            display: "flex",
            alignItems: "end",
            justifyContent: "center"
          }}
        >
          <img src={placeHolderImage} style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: isSmallScreen ? "contain" : "cover",
            borderRadius: "10px",
            zIndex: 1
          }} />
          <div id="video-layer" ref={videoLayerRef} style={{ position: "absolute", inset: 0, zIndex: 1 }} />
        </div>
        {isVideoLoading &&
          <div className="centered-box loader-container" style={{ position: "absolute", zIndex: 5 }}><div className="spinner" /> </div>
        }
        {/* {heygenError && !avatarReady && 
        (<div className="centered-box" style={{ zIndex: 4, top: '70%' }}><p style={{ fontWeight: 500 }}>Avatar load failed: {heygenError}</p></div>)
        } */}
      </div>
      {showDesktopControls &&
        <div className="mute_btn_wrap">
          {(canClose && !canReconnect) && (
            <button className="main-btn cst_close_consl_btn" onClick={() => setShowCloseConfirmModal(true)}>
              Close consulatation
            </button>
          )}
          {canReconnect &&
            (<button
              className="retry-btn text-white"
              onClick={restartLiveAvatarSession}
              disabled={isVideoLoading}>
              {!isVideoLoading ? "Reconnect" : "Loading..."}
            </button>)
          }
        </div>
      }
    </div>
  );
}

export default AvatarVideo;